import type { CSSProperties } from 'react'
import type { EmailSearchResult } from '@/types/emailSearch'
import type { FieldDefinition, FieldOption } from '@/tableControls'
import { readStoredColumnWidths, saveColumnWidths } from '@/tableControls'

export const EMAIL_SEARCH_RESULTS_PAGE_KEY = 'email-search-results'

export const LEGACY_WIDTH_KEYS = [
  'bookkeeper:email-search-results-widths',
  'invoice-minion:email-search-results-widths',
] as const

export const ALL_COLUMN_KEYS = [
  'select',
  'inbox',
  'from_address',
  'date',
  'subject',
  'preview',
  'has_attachment',
  'email_status',
  'row_actions',
] as const

export type EmailSearchColumnKey = (typeof ALL_COLUMN_KEYS)[number]

export const EMAIL_SEARCH_DEFAULT_WIDTHS: Record<string, number> = {
  select: 44,
  inbox: 112,
  from_address: 156,
  date: 120,
  subject: 212,
  preview: 280,
  has_attachment: 88,
  email_status: 168,
  row_actions: 64,
}

export const EMAIL_SEARCH_COLUMN_LABELS: Record<EmailSearchColumnKey, string> = {
  select: '',
  inbox: 'Inbox',
  from_address: 'From',
  date: 'Date',
  subject: 'Subject',
  preview: 'Preview',
  has_attachment: 'Attachment',
  email_status: 'Status',
  row_actions: 'Actions',
}

const LOCKED_COLUMN_KEYS = new Set<EmailSearchColumnKey>(['select', 'row_actions', 'subject'])

const SORTABLE_COLUMN_KEYS = new Set<EmailSearchColumnKey>([
  'inbox',
  'from_address',
  'date',
  'subject',
  'preview',
  'has_attachment',
])

export const EMAIL_SEARCH_DEFAULT_SORT = [{ field: 'date', direction: 'desc' as const }]

export const EMAIL_SEARCH_FIELD_DEFINITIONS: FieldDefinition[] = ALL_COLUMN_KEYS.map((key) => {
  if (key === 'preview') {
    return {
      key,
      label: EMAIL_SEARCH_COLUMN_LABELS[key],
      type: 'text',
      locked: LOCKED_COLUMN_KEYS.has(key),
      defaultVisible: true,
      sortable: SORTABLE_COLUMN_KEYS.has(key),
      accessor: (row: unknown) => (row as EmailSearchResult).snippet,
    }
  }
  if (key === 'has_attachment') {
    return {
      key,
      label: EMAIL_SEARCH_COLUMN_LABELS[key],
      type: 'boolean',
      locked: LOCKED_COLUMN_KEYS.has(key),
      defaultVisible: true,
      sortable: SORTABLE_COLUMN_KEYS.has(key),
      accessor: (row: unknown) => (row as EmailSearchResult).has_attachment,
    }
  }
  const rowKey =
    key === 'from_address'
      ? 'from_address'
      : key === 'email_status'
        ? 'message_id'
        : key === 'row_actions'
          ? 'message_id'
          : key === 'select'
            ? 'message_id'
            : (key as keyof EmailSearchResult)
  return {
    key,
    label: EMAIL_SEARCH_COLUMN_LABELS[key],
    type: key === 'date' ? 'date' : 'text',
    locked: LOCKED_COLUMN_KEYS.has(key),
    defaultVisible: true,
    sortable: SORTABLE_COLUMN_KEYS.has(key),
    ...(key !== 'select' && key !== 'row_actions' && key !== 'email_status'
      ? { accessor: (row: unknown) => (row as EmailSearchResult)[rowKey as keyof EmailSearchResult] }
      : {}),
  }
})

export function fixedColumnStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
  }
}

export function migrateLegacyColumnWidths(): void {
  const existing = readStoredColumnWidths(EMAIL_SEARCH_RESULTS_PAGE_KEY)
  if (Object.keys(existing).length > 0) return
  for (const legacyKey of LEGACY_WIDTH_KEYS) {
    try {
      const raw = localStorage.getItem(legacyKey)
      if (!raw) continue
      const parsed = JSON.parse(raw) as Record<string, unknown>
      const widths: Record<string, number> = {}
      for (const key of ALL_COLUMN_KEYS) {
        const value = parsed[key]
        if (typeof value === 'number' && Number.isFinite(value)) {
          widths[key] = value
        }
      }
      if (Object.keys(widths).length > 0) {
        saveColumnWidths(EMAIL_SEARCH_RESULTS_PAGE_KEY, widths)
      }
      return
    } catch {
      /* try next legacy key */
    }
  }
}

export function isEmailColumnSortable(key: string): boolean {
  return SORTABLE_COLUMN_KEYS.has(key as EmailSearchColumnKey)
}

export const DOC_REVIEW_STATUS_FILTER_OPTIONS: FieldOption[] = [
  { value: 'new', label: 'New only' },
  { value: 'processed', label: 'Processed' },
]

/** FilterBuilderDrawer fields (table filters only — not column picker / sort). */
export function buildEmailSearchFilterFields(
  inboxOptions: FieldOption[]
): FieldDefinition[] {
  return [
    {
      key: 'inbox',
      label: 'Inbox',
      type: 'multiSelect',
      filterable: true,
      options: inboxOptions,
      accessor: (row) => (row as EmailSearchResult).inbox ?? '',
    },
    {
      key: 'has_attachment',
      label: 'Attachment',
      type: 'boolean',
      filterable: true,
      accessor: (row) => (row as EmailSearchResult).has_attachment,
    },
    {
      key: 'doc_review_status',
      label: 'Doc Review status',
      type: 'select',
      filterable: true,
      options: DOC_REVIEW_STATUS_FILTER_OPTIONS,
    },
  ]
}
